// Canvas recolor of the white AI render: only the body paint, rims and calipers change — background stays untouched
const load = (src) => new Promise((res, rej) => { const i = new Image(); i.crossOrigin = "anonymous"; i.onload = () => res(i); i.onerror = rej; i.src = src; });
const rgb = (h) => [1, 3, 5].map((i) => parseInt((h || "#888888").slice(i, i + 2), 16) / 255);
const smooth = (v, a, b) => { const t = Math.max(0, Math.min(1, (v - a) / (b - a))); return t * t * (3 - 2 * t); };
const lerp = (a, b, t) => a + (b - a) * t;

export const prepare = async (src) => {
  const img = await load(src);
  const w = img.naturalWidth;
  const h = img.naturalHeight;
  const cv = document.createElement("canvas");
  cv.width = w; cv.height = h;
  const ctx = cv.getContext("2d");
  ctx.drawImage(img, 0, 0);
  const px = ctx.getImageData(0, 0, w, h);
  const lum = new Float32Array(w * h);
  const body = new Float32Array(w * h);
  for (let i = 0; i < w * h; i += 1) {
    const [r, g, b] = [px.data[i * 4] / 255, px.data[i * 4 + 1] / 255, px.data[i * 4 + 2] / 255];
    const l = 0.299 * r + 0.587 * g + 0.114 * b;
    lum[i] = l;
    body[i] = smooth(l, 0.1, 0.32) * (1 - smooth(Math.max(r, g, b) - Math.min(r, g, b), 0.3, 0.5));
  }
  return { w, h, px, lum, body };
};

const ellipses = (boxes, w, h) => (boxes || []).map((b) => ({ cx: ((b.x + b.w / 2) / 100) * w, cy: ((b.y + b.h / 2) / 100) * h, rx: (b.w / 200) * w, ry: (b.h / 200) * h, box: b }));
const inside = (e, x, y) => ((x - e.cx) / e.rx) ** 2 + ((y - e.cy) / e.ry) ** 2;

export const paint = (p, { color, wheels, calipers, glow, parts }) => {
  const { w, h, px, lum, body } = p;
  const out = new ImageData(new Uint8ClampedArray(px.data), w, h);
  const d = out.data;
  const [c1, c2] = [rgb(color?.hex), rgb(color?.hex2 || color?.hex)];
  const fin = color?.finish;
  const W = ellipses(parts?.wheels, w, h);
  const C = ellipses(parts?.calipers, w, h);
  const floorY = W.length ? Math.max(...W.map((e) => e.cy + e.ry)) : h * 0.8;
  const [gx0, gx1] = W.length ? [Math.min(...W.map((e) => e.cx - e.rx)), Math.max(...W.map((e) => e.cx + e.rx))] : [w * 0.2, w * 0.8];
  const [wc, cc, gc] = [wheels && rgb(wheels), calipers && rgb(calipers), glow && rgb(glow)];
  for (let y = 0; y < h; y += 1) {
    for (let x = 0; x < w; x += 1) {
      const i = y * w + x;
      const k = i * 4;
      const l = lum[i];
      let [r, g, b] = [d[k] / 255, d[k + 1] / 255, d[k + 2] / 255];
      let wd = 9;
      for (let j = 0; j < W.length; j += 1) { const v = inside(W[j], x, y); if (v < wd) wd = v; }
      let m = color ? body[i] * (wd < 0.45 || (wd < 1.1 && l < 0.4) ? 0 : 1) * (y > floorY ? 0.3 : 1) : 0;
      if (m > 0) {
        const t = fin === "colorshift" || fin === "iridescent" ? 0.5 + 0.5 * Math.sin((x / w) * 5 + (y / h) * 3) : 0;
        let c = [lerp(c1[0], c2[0], t), lerp(c1[1], c2[1], t), lerp(c1[2], c2[2], t)];
        if (fin === "iridescent") c = c.map((v, j) => lerp(v, [0.97, 0.84, 1][j], 0.25 * (1 - t)));
        const shade = fin === "chrome" ? Math.min(1.6, l ** 2.2 * 1.9 + 0.05) : fin === "matte" ? 0.35 + 0.6 * smooth(l, 0.3, 0.95) : l * 1.04;
        const spec = fin === "matte" ? 0 : smooth(l, fin === "chrome" ? 0.85 : 0.96, 1) * (fin === "satin" ? 0.15 : fin === "chrome" ? 0.7 : 0.35);
        const tr = [c[0] * shade, c[1] * shade, c[2] * shade].map((v) => Math.min(1, v + spec * (1 - v)));
        [r, g, b] = [lerp(r, tr[0], m), lerp(g, tr[1], m), lerp(b, tr[2], m)];
      }
      if (wc && wd < 0.55 && l < 0.85) {
        m = 0.9 * (1 - smooth(wd, 0.4, 0.55));
        [r, g, b] = [r, g, b].map((v, j) => lerp(v, Math.min(1, wc[j] * (0.22 + 1.3 * l)), m));
      }
      if (cc && l > 0.1 && l < 0.75) {
        let cd = 9;
        for (let j = 0; j < C.length; j += 1) { const v = inside(C[j], x, y); if (v < cd) cd = v; }
        const a = 0.6 * (1 - smooth(cd, 0.35, 0.85));
        if (a > 0) [r, g, b] = [r, g, b].map((v, j) => lerp(v, Math.min(1, cc[j] * (0.45 + l)), a));
      }
      if (gc && y > floorY - h * 0.05 && x > gx0 - w * 0.04 && x < gx1 + w * 0.04) {
        const e = ((x - (gx0 + gx1) / 2) / ((gx1 - gx0) * 0.62)) ** 2 + ((y - floorY) / (h * 0.06)) ** 2;
        const a = Math.max(0, 1 - e) ** 1.5 * 0.9 * smooth(y, floorY - h * 0.05, floorY + h * 0.01);
        [r, g, b] = [r + gc[0] * a, g + gc[1] * a, b + gc[2] * a].map((v) => Math.min(1, v));
      }
      d[k] = r * 255; d[k + 1] = g * 255; d[k + 2] = b * 255;
    }
  }
  const cv = document.createElement("canvas");
  cv.width = w; cv.height = h;
  cv.getContext("2d").putImageData(out, 0, 0);
  return cv;
};

// Shareable card: the build picture + name, wrap, add-ons and total
export const shareCard = async (src, { title, lines, total }) => {
  const img = typeof src === "string" ? await load(src) : src;
  const w = img.width;
  const h = img.height;
  const cv = document.createElement("canvas");
  cv.width = w; cv.height = h + 300;
  const ctx = cv.getContext("2d");
  ctx.fillStyle = "#050607"; ctx.fillRect(0, 0, w, h + 300);
  ctx.drawImage(img, 0, 0, w, h);
  ctx.fillStyle = "#38bdf8"; ctx.font = "600 26px Outfit, sans-serif"; ctx.fillText("MY DREAM BUILD · REFLECTIONS AUTO DETAILING", 48, h + 60);
  ctx.fillStyle = "#ffffff"; ctx.font = "500 54px Outfit, sans-serif"; ctx.fillText(title, 48, h + 128);
  ctx.fillStyle = "#9ca3af"; ctx.font = "400 26px Outfit, sans-serif"; ctx.fillText(lines.join(" · ").slice(0, 110), 48, h + 178);
  ctx.fillStyle = "#ffffff"; ctx.font = "600 64px Outfit, sans-serif"; ctx.textAlign = "right"; ctx.fillText(total, w - 48, h + 250);
  ctx.textAlign = "left"; ctx.fillStyle = "#6b7280"; ctx.font = "400 24px Outfit, sans-serif"; ctx.fillText("Maricopa, AZ · Build yours on our Services page", 48, h + 250);
  return new Promise((res) => cv.toBlob(res, "image/jpeg", 0.92));
};
